
import { defineComponent, ref, onMounted, computed, watch } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage } from "element-plus"
import { Plus } from '@element-plus/icons-vue'
import type { ElForm } from "element-plus"
import { CardSeriesOption } from "@/store/modules/CardSeriesModule"
import { CardRarityOption } from "@/store/modules/CardModule"
import { CardLanguageOption } from "@/store/modules/CardLanguageModule"
import { CardVersionOption } from "@/store/modules/CardVersionModule"

type FormInstance = InstanceType<typeof ElForm>;

interface CardForm {
  card_series_id: number | null;
  name: string;
  card_rarity_id: number | null;
  card_language_id: number | null;
  card_version_id: number | null;
  image: string | null;
  description: string | null;
  recycle_price: number;
  sort: number;
  enabled: boolean;
}

export default defineComponent({
  components: {
    Plus
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<FormInstance>()

    const isNew = !route.params.id

    const cardSeriesOptions = ref<CardSeriesOption[]>([])
    const rarityOptions = ref<CardRarityOption[]>([])
    const languageOptions = ref<CardLanguageOption[]>([])
    const versionOptions = ref<CardVersionOption[]>([])

    onMounted(() => {
      if (isNew) {
        setCurrentPageBreadcrumbs("新建卡牌", ["抽卡系统", "卡牌管理;cards_management"])
      } else {
        setCurrentPageBreadcrumbs("编辑卡牌", ["抽卡系统", "卡牌管理;cards_management"])
      }
      loadOptions()
    })

    const card = ref<CardForm>({
      card_series_id: null,
      name: '',
      card_rarity_id: null,
      card_language_id: null,
      card_version_id: null,
      image: null,
      description: null,
      recycle_price: 0,
      sort: 0,
      enabled: true
    })

    const rules = ref({
      card_series_id: [
        { required: true, message: '请选择所属系列', trigger: 'change' }
      ],
      name: [
        { required: true, message: '请输入卡牌名称', trigger: 'blur' },
        { min: 1, max: 100, message: '名称长度需在 1-100 之间', trigger: 'blur' }
      ],
      card_rarity_id: [
        { required: true, message: '请选择稀有度', trigger: 'change' }
      ]
    })

    const loadVersionOptions = (cardLanguageId?: number | null) => {
      const params = cardLanguageId ? { card_language_id: cardLanguageId } : undefined
      store.dispatch(Actions.GET_ALL_CARD_VERSIONS, params).then(() => {
        versionOptions.value = store.getters.currentAllCardVersions
      })
    }

    const loadOptions = () => {
      // 加载系列选项
      store.dispatch(Actions.GET_ALL_CARD_SERIES).then(() => {
        cardSeriesOptions.value = store.getters.currentAllCardSeries
      })
      // 加载稀有度选项
      store.dispatch(Actions.GET_CARD_RARITIES).then(() => {
        rarityOptions.value = store.getters.currentCardRarities
      })
      // 加载语系选项
      store.dispatch(Actions.GET_ALL_CARD_LANGUAGES).then(() => {
        languageOptions.value = store.getters.currentAllCardLanguages
      })
      // 加载版本选项（初始加载全部，编辑模式会按语系加载）
      loadVersionOptions()
    }

    // 监听语系变化，级联刷新版本选项
    watch(() => card.value.card_language_id, (newVal, oldVal) => {
      // 仅在用户主动变更语系时处理（oldVal !== undefined 排除初始化）
      if (oldVal !== undefined) {
        loadVersionOptions(newVal)
        // 如果当前选中的版本不属于新语系，清空版本选择
        if (card.value.card_version_id && newVal) {
          const valid = versionOptions.value.some(
            v => v.id === card.value.card_version_id && v.card_language_id === newVal
          )
          if (!valid) {
            card.value.card_version_id = null
          }
        }
      }
    })

    // 图片上传相关（base64 方式，随表单提交）
    const imageUrl = ref('')

    const onImageChange = (file: { raw: File }) => {
      const rawFile = file.raw
      if (!rawFile.type.startsWith('image/')) {
        ElMessage.error('只能上传图片文件!')
        return
      }
      if (rawFile.size / 1024 / 1024 > 2) {
        ElMessage.error('图片大小不能超过 2MB!')
        return
      }
      const reader = new FileReader()
      reader.readAsDataURL(rawFile)
      reader.onload = (e) => {
        imageUrl.value = e.target?.result?.toString() || ''
        card.value.image = imageUrl.value
      }
    }

    // 加载详情（编辑模式）
    if (!isNew) {
      store
        .dispatch(Actions.GET_CARD, route.params.id)
        .then(() => {
          const data = store.getters.currentCard
          card.value = {
            card_series_id: data.card_series_id,
            name: data.name,
            card_rarity_id: data.card_rarity_id,
            card_language_id: data.card_language_id,
            card_version_id: data.card_version_id,
            image: data.image,
            description: data.description,
            recycle_price: data.recycle_price || 0,
            sort: data.sort || 0,
            enabled: data.enabled
          }
          if (data.image) {
            imageUrl.value = data.image
          }
          // 编辑模式：按语系加载版本选项以正确回显
          if (data.card_language_id) {
            loadVersionOptions(data.card_language_id)
          }
        })
        .catch(() => {
          ElMessage.error('获取卡牌详情失败')
        })
    }

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return

      formEl.validate((valid) => {
        if (valid) {
          const params = { ...card.value }

          if (isNew) {
            store
              .dispatch(Actions.CREATE_CARD, params)
              .then((data: { success: boolean; error_msg?: string }) => {
                if (data.success) {
                  ElMessage.success('创建成功')
                  router.push({ name: 'cards_management' })
                } else {
                  ElMessage.error(data.error_msg || '创建失败')
                }
              })
              .catch(() => {
                ElMessage.error('创建失败')
              })
          } else {
            store
              .dispatch(Actions.UPDATE_CARD, {
                id: route.params.id,
                values: params
              })
              .then((data: { success: boolean; error_msg?: string }) => {
                if (data.success) {
                  ElMessage.success('更新成功')
                  router.push({ name: 'cards_management' })
                } else {
                  ElMessage.error(data.error_msg || '更新失败')
                }
              })
              .catch(() => {
                ElMessage.error('更新失败')
              })
          }
        }
      })
    }

    const onCancel = () => {
      router.push({ name: 'cards_management' })
    }

    return {
      formRef,
      card,
      rules,
      isNew,
      cardSeriesOptions,
      rarityOptions,
      languageOptions,
      versionOptions,
      imageUrl,
      onImageChange,
      onSubmit,
      onCancel
    }
  }
})
